<template>
  <div class="catalog-child-categories-light-cards-list">
    <div
      v-if="childCategories && childCategories.length > 0 && $viewport?.isGreaterOrEquals('md')"
      ref="swiperContainer"
      class="catalog-child-categories-light-cards-list__swiper swiper"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(subCategory, index) in childCategories"
          :key="`${subCategory.slug}-${subCategory.id}-${index}`"
          class="swiper-slide"
        >
          <SmallMenuCategoryCard :item="subCategory"/>
        </div>
      </div>
    </div>
    <div
      v-if="childCategories && $viewport?.isLessThan('md')"
      class="catalog-child-categories-light-cards-list_mobile"
    >
      <div v-for="subCategory in childCategories" :key="subCategory.id">
        <SmallMenuCategoryCard :item="subCategory"/>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {Category, type ICategory} from "#sitis/internal/controllers/categories/models/Category";
import SmallMenuCategoryCard from "~/components/catalog/light-children-menu-categories/SmallMenuCategoryCard.vue";
import Swiper from 'swiper';

const {
  $viewport
} = useNuxtApp();

interface IProps {
  category: ICategory | null,
}
const props = withDefaults(defineProps<IProps>(), {
  category: null
});

// Оптимизировано: убрано дублирование массива
const childCategories = computed(() => {
  if (!props.category?.dynamicFields?.children) {
    return [];
  }
  return props.category.dynamicFields.children.map((item: ICategory) => new Category(item));
})

const swiperContainer = ref<HTMLElement | null>(null);
let swiperInstance: Swiper | null = null;
let updateTimeout: NodeJS.Timeout | null = null;

const initSwiper = () => {
  if (!swiperContainer.value || !$viewport?.isGreaterOrEquals('md')) {
    return;
  }

  // Оптимизация: обновляем swiper вместо полного пересоздания, если он уже существует
  if (swiperInstance) {
    swiperInstance.update();
    return;
  }

  swiperInstance = new Swiper(swiperContainer.value, {
    spaceBetween: 16,
    slidesPerView: 2.2,
    init: true,
    observer: true, // Автоматическое обновление при изменении DOM
    observeParents: true,
    breakpoints: {
      1360: { slidesPerView: 'auto' },
      860: { slidesPerView: 3.6 },
      640: { slidesPerView: 3.7 },
      520: { slidesPerView: 3 }
    }
  });
};

const destroySwiper = () => {
  if (swiperInstance) {
    swiperInstance.destroy(true, true);
    swiperInstance = null;
  }
};

onMounted(() => {
  // Используем requestIdleCallback для неблокирующей инициализации
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      nextTick(() => {
        initSwiper();
      });
    });
  } else {
    nextTick(() => {
      initSwiper();
    });
  }
});

onBeforeUnmount(() => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }
  destroySwiper();
});

// Оптимизировано: добавлен debounce для watch
watch(() => childCategories.value, () => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }

  updateTimeout = setTimeout(() => {
    nextTick(() => {
      if (swiperInstance) {
        swiperInstance.update();
      } else {
        initSwiper();
      }
    });
  }, 150);
}, { deep: false });
</script>

<style scoped lang="scss">
.catalog-child-categories-light-cards-list {
  padding: 40px 0;

  // Оптимизация: CSS containment для изоляции рендеринга
  contain: layout style;
  content-visibility: auto;

  .catalog-child-categories-light-cards-list__swiper {
    height: initial;
    .swiper-slide {
      height: initial;
      max-width: 211px;
      min-width: 208px;
      margin-right: 16px;

      // Оптимизация: containment для слайдов
      contain: layout paint;
    }
    & > * {
      max-width: 211px;
      position: relative;
    }
  }
}
@media screen and (max-width: 860px) {
  .catalog-child-categories-light-cards-list {
    margin-right: -20px;
  }
  .catalog-child-categories-light-cards-list_mobile {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;

    -ms-overflow-style: none;
    scrollbar-width: none;

    -webkit-overflow-scrolling: touch;

    // Оптимизация: CSS containment
    contain: layout style;

    &::-webkit-scrollbar {
      display: none;
    }

    & > * {
      min-width: 181px;
      height: initial;

      scroll-snap-align: start;
      backface-visibility: hidden;
      cursor: grab;

      // Оптимизация: использовать transform с GPU acceleration
      transform: translateZ(0);
      transition: transform 0.2s ease;

      // Оптимизация: будет применяться только при взаимодействии
      &:active {
        cursor: grabbing;
        transform: scale(0.98) translateZ(0);
        will-change: transform;
      }
    }
  }
}
</style>
